import { WalletBalance } from '@/components/billing/WalletBalance'
import { RequireAccess } from '@/components/require-access'
import { useAtsDeactivation, useIsJobillan, useJobillaOrgId } from '@/hooks'
import { useAuth, useEntitlements } from '@/lib'
import { LinkFunction, LinkLocalProps, ProvidedSettingsNavConfigContext, SettingsNavContext, Tooltip } from '@/ui'
import { EarlyAccessBadge } from '@/ui/constructs/gate'
import { JobillanBadge } from '@/ui/core/jobillan-badge'
import {
	roleEnum,
	stripeFeature,
	useCustomerValidateBillingDetails,
	useCustomerWalletShow,
	useInvoiceSettingsShow,
	useInvoiceSummary,
	useProfilesIndex,
} from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { LockIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { ReactNode, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { OutPortal } from 'react-reverse-portal'
import {
	AdDesignsIcon,
	AiSettingsIcon,
	AssetHubIcon,
	BrandingLibraryIcon,
	CampaignSettingsIcon,
	CandidateCenterIcon,
	CareerSiteIcon,
	ChevronIcon,
	CustomFieldsIcon,
	DesignSimulatorIcon,
	EmailTemplatesIcon,
	InventoryIcon,
	InvoicesIcon,
	MembersIcon,
	OrganizationIcon,
	PrivacyIcon,
	ProfilesIcon,
	SettingsExportIcon,
	SettingsIndexIcon,
	WalletIcon,
	WhatsAppIcon,
} from './settings-nav-icons'
export interface SettingsNavProps {
	children: ReactNode
	active?:
		| 'members'
		| 'export'
		| 'branding-library'
		| 'asset-hub'
		| 'inventory'
		| 'invoices'
		| 'wallet'
		| 'billing'
		| 'organization'
		| 'privacy'
		| 'campaign-settings'
		| 'candidate-center'
		| 'whatsapp'
		| 'custom-fields'
		| 'email-templates'
		| 'fonts'
		| 'design-simulator'
		| 'ad-designs'
		| 'external-connections'
		| 'career-site'
		| 'ai-settings'
	fullWidth?: boolean
	fixedHeight?: boolean
}

// This one actually gets rendered.
export function SettingsNav({ children, active, fullWidth = false, fixedHeight = false }: SettingsNavProps) {
	const { settingsNavTargetNode } = useContext(SettingsNavContext)
	if (!settingsNavTargetNode) {
		throw new Error('No settings nav context')
	}

	// The min-h there is important to prevent a collapse when acting as a grid child.
	return (
		<div
			className={ctl(`
				grid ${fullWidth ? 'grid-cols-[300px_1fr]' : 'grid-cols-[1fr_minmax(0,800px)_1fr]'}
				transition-all duration-300
				min-h-[calc(var(--main-nav-height)+1px)] mx-auto w-full
				relative
			`)}
		>
			<div className="w-[300px] min-w-[300px] sticky top-[calc(var(--main-nav-height)+1px)] h-[calc(100vh-1px-var(--main-nav-height))] overflow-y-scroll border-r-[1px] border-grey-100">
				<div className="flex flex-col h-full">
					<OutPortal<typeof ReusedInnerSettingsNav> node={settingsNavTargetNode} active={active} />
				</div>
			</div>
			<div
				className={ctl(`
					bg-white
					flex flex-col gap-4 text-grey-800
					w-full
					${fullWidth ? 'pl-0' : 'px-8 pb-20'}
					[--available-height:calc(100vh-var(--main-nav-height)-1px)]
					${fixedHeight ? 'h-[var(--available-height)]' : ''}
				`)}
			>
				{children}
			</div>
			<div />
		</div>
	)
}

// This one is reverse-portaled, and instantiated in `settings-wrapper.tsx`.
export function ReusedInnerSettingsNav({ active }: { active?: SettingsNavProps['active'] }) {
	const { t, i18n } = useTranslation('common')
	const isJobillan = useIsJobillan()
	const { can } = useEntitlements()
	const { can: hasPermission } = useAuth()
	const organisationId = useJobillaOrgId()
	const atsDeactivation = useAtsDeactivation()
	const { data: wallet } = useCustomerWalletShow(organisationId, {
		enabled: !!organisationId && hasPermission('billing:read'),
		staleTime: 30 * 1000,
	})
	const { data: billingValidation } = useCustomerValidateBillingDetails(organisationId, undefined, {
		enabled: !!organisationId && hasPermission('billing:read'),
		staleTime: 5 * 60 * 1000,
	})
	const walletCurrencyFormatter = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' })
	const walletTaxPercentage = billingValidation?.customer?.tax?.taxRate.percentage ?? 0
	const canViewInvoices = hasPermission('invoice:view')
	const showBillingGroup = hasPermission('billing:read')
		|| hasPermission('inventory:view')
		|| canViewInvoices
		|| hasPermission('custom-fields:read')
	const showAssetsGroup = hasPermission('media:read') || isJobillan || hasPermission('branding:read')
	const { data: invoiceSettings } = useInvoiceSettingsShow(organisationId, {
		enabled: !!organisationId && canViewInvoices,
		staleTime: 5 * 60 * 1000,
	})
	const profilesQuery = useProfilesIndex(organisationId, {
		enabled: !!organisationId && hasPermission('profile:view'),
		staleTime: 5 * 60 * 1000,
	})
	// Until a billing profile exists, the downstream billing tabs have nothing to
	// operate on, so hide them and flag Billing as the required first step. Only
	// act on a definitive (loaded) empty result — never hide while loading or when
	// we lack permission to know.
	const hasNoBillingProfile = profilesQuery.isSuccess && (profilesQuery.data?.data.length ?? 0) === 0

	const externalInvoicing = invoiceSettings?.externalInvoicing ?? false
	const { data: invoiceSummary } = useInvoiceSummary(organisationId, {
		enabled: !!organisationId && canViewInvoices && !externalInvoicing,
		staleTime: 5 * 60 * 1000,
	})
	// `summary.unpaid.count` includes all open invoices (overdue + non-overdue);
	// the badge splits them, so subtract overdue to get the non-overdue portion.
	const overdueInvoiceCount = invoiceSummary?.overdue.count ?? 0
	const unpaidInvoiceCount = (invoiceSummary?.unpaid.count ?? 0) - overdueInvoiceCount
	const showInvoiceBadge = !externalInvoicing && (unpaidInvoiceCount > 0 || overdueInvoiceCount > 0)
	const {
		organizationLinker,
		membersLinker,
		privacyLinker,
		campaignSettingsLinker,
		candidateCenterLinker,
		customFieldLinker,
		exportLinker,
		brandingLibraryLinker,
		assetHubLinker,
		inventoryLinker,
		invoicesLinker,
		walletLinker,
		billingLinker,
		whatsappLinker,
		emailTemplatesLinker,
		fontsLinker,
		designSimulatorLinker,
		adDesignsLinker,
		externalConnectionsLinker,
		careerSiteLinker,
		aiSettingsLinker,
	} = useContext(
		ProvidedSettingsNavConfigContext,
	)

	return (
		<div className="pl-4 pt-4">
			<SettingsNavGroup label={t('settingsNav.group.organization')}>
				<SettingsNavItem
					active={active === 'organization'}
					linker={organizationLinker}
					icon={<OrganizationIcon />}
				>
					{t('settingsNav.organizationSettings')}
				</SettingsNavItem>
				{hasPermission('organisation:remove-member') && (
					<SettingsNavItem
						active={active === 'members'}
						linker={membersLinker}
						icon={<MembersIcon />}
					>
						{t('settingsNav.members')}
					</SettingsNavItem>
				)}
				<RequireAccess role={[roleEnum.admin, roleEnum.jobillan]}>
					<SettingsNavItem active={active === 'privacy'} linker={privacyLinker} icon={<PrivacyIcon />}>
						{t('settingsNav.privacySettings')}
					</SettingsNavItem>
				</RequireAccess>
				<SettingsNavItem
					icon={<AiSettingsIcon />}
					linker={aiSettingsLinker}
					active={active === 'ai-settings'}
				>
					{t('settingsNav.aiSettings')}
				</SettingsNavItem>
			</SettingsNavGroup>

			<SettingsNavGroup label={t('settingsNav.group.campaigns')}>
				{hasPermission('campaign:update') && (
					<SettingsNavItem
						active={active === 'campaign-settings'}
						linker={campaignSettingsLinker}
						icon={<CampaignSettingsIcon />}
					>
						{t('settingsNav.campaignSettings')}
					</SettingsNavItem>
				)}
				<RequireAccess role={[roleEnum.admin, roleEnum.jobillan]}>
					<SettingsNavItem
						active={active === 'candidate-center'}
						linker={candidateCenterLinker}
						icon={<CandidateCenterIcon />}
					>
						{t('settingsNav.candidateCenter')}
					</SettingsNavItem>
				</RequireAccess>
				{hasPermission('campaign:update') && (
					<SettingsNavItem
						active={active === 'career-site'}
						linker={careerSiteLinker}
						icon={<CareerSiteIcon />}
					>
						{t('settingsNav.careerSite')}
					</SettingsNavItem>
				)}
				{hasPermission('campaign:create') && (
					<SettingsNavItem
						active={active === 'email-templates'}
						linker={emailTemplatesLinker}
						icon={<EmailTemplatesIcon />}
					>
						{t('settingsNav.emailTemplates')}
					</SettingsNavItem>
				)}
				<SettingsNavItem
					linker={whatsappLinker}
					active={active === 'whatsapp'}
					icon={<WhatsAppIcon />}
					badge={<EarlyAccessBadge iconOnly />}
				>
					{t('settingsNav.whatsappBusiness')}
				</SettingsNavItem>
			</SettingsNavGroup>

			{showAssetsGroup && (
				<SettingsNavGroup label={t('settingsNav.group.assets')}>
					{hasPermission('media:read') && (
						<SettingsNavItem
							icon={<AssetHubIcon />}
							linker={assetHubLinker}
							active={active === 'asset-hub'}
						>
							{t('settingsNav.assetHub')}
						</SettingsNavItem>
					)}
					{isJobillan
						? (
							<SettingsNavItem
								icon={<BrandingLibraryIcon />}
								linker={fontsLinker}
								active={active === 'fonts'}
								badge={<JobillanBadge />}
							>
								{t('settingsNav.customFonts')}
							</SettingsNavItem>
						)
						: null}
					{hasPermission('branding:read') && (
						<SettingsNavItem
							icon={<BrandingLibraryIcon />}
							linker={brandingLibraryLinker}
							active={active === 'branding-library'}
						>
							{t('settingsNav.brandingLibrary')}
						</SettingsNavItem>
					)}
				</SettingsNavGroup>
			)}

			{showBillingGroup && (
				<SettingsNavGroup label={t('settingsNav.group.billing')}>
					{hasPermission('billing:read') && (
						<SettingsNavItem
							linker={billingLinker}
							active={active === 'billing'}
							icon={<ProfilesIcon />}
							badge={hasNoBillingProfile ? <RequiredBadge /> : undefined}
						>
							{t('settingsNav.billing')}
						</SettingsNavItem>
					)}
					{hasPermission('billing:read') && !hasNoBillingProfile && !externalInvoicing && (
						<SettingsNavItem
							linker={walletLinker}
							active={active === 'wallet'}
							icon={<WalletIcon />}
							badge={wallet
								? (
									<WalletBalance
										balanceCents={wallet.balance}
										taxPercentage={walletTaxPercentage}
										currencyFormatter={walletCurrencyFormatter}
										className="text-xs tabular-nums font-medium"
										mode="hover-swap"
									/>
								)
								: null}
						>
							{t('settingsNav.wallet')}
						</SettingsNavItem>
					)}
					{hasPermission('inventory:view') && !hasNoBillingProfile && !externalInvoicing && (
						<SettingsNavItem
							linker={inventoryLinker}
							active={active === 'inventory'}
							icon={<InventoryIcon />}
						>
							{t('settingsNav.inventory')}
						</SettingsNavItem>
					)}
					{canViewInvoices && !hasNoBillingProfile && !externalInvoicing && (
						<SettingsNavItem
							linker={invoicesLinker}
							active={active === 'invoices'}
							icon={<InvoicesIcon />}
							badge={showInvoiceBadge
								? (
									<InvoiceStatusBadge
										unpaid={unpaidInvoiceCount}
										overdue={overdueInvoiceCount}
										unpaidAmount={invoiceSummary?.unpaid.amount ?? 0}
										overdueAmount={invoiceSummary?.overdue.amount ?? 0}
										currency={invoiceSummary?.unpaid.currency ?? 'eur'}
									/>
								)
								: undefined}
						>
							{t('settingsNav.invoices')}
						</SettingsNavItem>
					)}
					{hasPermission('custom-fields:read') && (
						<SettingsNavItem
							icon={<CustomFieldsIcon />}
							linker={customFieldLinker}
							active={active === 'custom-fields'}
						>
							{t('settings.customFields.title')}
						</SettingsNavItem>
					)}
				</SettingsNavGroup>
			)}

			<SettingsNavGroup label={t('settingsNav.group.dataAndAnalytics')}>
				<SettingsNavItem
					icon={<SettingsExportIcon />}
					linker={exportLinker}
					active={active === 'export'}
					locked={!can(stripeFeature['addon-ats-integration'])}
					badge={atsDeactivation
						? (
							<Tooltip content={t('settingsNav.exportCenterAttention')}>
								<span
									aria-label={t('settingsNav.exportCenterAttention')}
									className="block size-2 rounded-full bg-amber-500"
								/>
							</Tooltip>
						)
						: undefined}
				>
					{t('settingsNav.exportCenter')}
				</SettingsNavItem>
				{isJobillan && (
					<SettingsNavItem
						linker={externalConnectionsLinker}
						active={active === 'external-connections'}
						icon={<SettingsIndexIcon />}
						badge={<JobillanBadge />}
					>
						External Connections
					</SettingsNavItem>
				)}
			</SettingsNavGroup>

			{isJobillan && (
				<>
					<div className="h-[1px] bg-grey-100 mr-4 my-2" />
					<SettingsNavItem
						linker={designSimulatorLinker}
						active={active === 'design-simulator'}
						icon={<DesignSimulatorIcon />}
						badge={<JobillanBadge />}
					>
						Design Simulator
					</SettingsNavItem>
					<SettingsNavItem
						icon={<AdDesignsIcon />}
						linker={adDesignsLinker}
						active={active === 'ad-designs'}
						badge={<JobillanBadge />}
					>
						Ad Designs
					</SettingsNavItem>
				</>
			)}
			<div className="h-8" />
		</div>
	)
}

function SettingsNavGroup({ label, children }: { label: string; children: ReactNode }) {
	const [expanded, setExpanded] = useState(true)

	return (
		<div className="mt-1">
			<button
				onClick={() => setExpanded(!expanded)}
				aria-expanded={expanded}
				className="flex items-center gap-1.5 px-2 py-1.5 w-full text-xs font-semibold uppercase tracking-wider text-grey-400 hover:text-grey-600 transition-colors"
			>
				<ChevronIcon expanded={expanded} />
				<span>{label}</span>
			</button>
			{expanded && <div className="pl-2">{children}</div>}
		</div>
	)
}

type SettingsNavItemProps = {
	linker: LinkFunction<LinkLocalProps>
	active: boolean
	children: ReactNode
	icon: ReactNode
	badge?: ReactNode
	locked?: boolean
}

function SettingsNavItem({ linker, active, children, icon, badge, locked }: SettingsNavItemProps) {
	const { t } = useTranslation('common')
	return (
		<div className="relative">
			{active && (
				<>
					<motion.div
						layoutId="active-nav-bar"
						className="absolute inset-y-0 right-0 z-1 w-[2px] bg-brand-600 rounded-full"
					/>
					<motion.div
						layoutId="active-nav-bg"
						className="absolute inset-0 bg-gradient-to-l from-brand-50 to-brand-50/0"
					/>
				</>
			)}
			{linker({
				children: (
					<>
						<span className={ctl(`${active ? 'text-brand-600' : 'text-grey-500'} transition-colors`)}>
							{icon}
						</span>
						<span className="flex-1">{children}</span>
						{locked && (
							<Tooltip content={t('entitlements.preview.upgradeRequired')}>
								<LockIcon className="size-3.5 mr-1 text-grey-400" />
							</Tooltip>
						)}
						{badge && (
							<div className={ctl(`mr-1 ${active ? 'text-brand-600' : 'text-grey-500'}`)}>{badge}</div>
						)}
					</>
				),
				className: ctl(
					`z-100 group/nav-item relative p-2 flex items-center gap-3 transition-colors ${
						active ? 'text-brand-600' : 'text-grey-800'
					}`,
				),
			})}
		</div>
	)
}

function RequiredBadge() {
	const { t } = useTranslation('common')
	return (
		<span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
			{t('settingsNav.profilesRequired')}
		</span>
	)
}

function InvoiceStatusBadge({
	unpaid,
	overdue,
	unpaidAmount,
	overdueAmount,
	currency,
}: {
	unpaid: number
	overdue: number
	unpaidAmount: number
	overdueAmount: number
	currency: string
}) {
	const { t, i18n } = useTranslation('common')

	const formatMoney = (cents: number) =>
		new Intl.NumberFormat(i18n.language, {
			style: 'currency',
			currency: currency.toUpperCase(),
		}).format(cents / 100)

	if (overdue > 0) {
		return (
			<span className="grid text-xs font-medium tabular-nums leading-tight text-red-600">
				<span className="col-start-1 row-start-1 transition-opacity duration-150 group-hover/nav-item:opacity-0">
					{t('settingsNav.invoicesOverdueBadge', { count: overdue })}
				</span>
				<span className="col-start-1 row-start-1 opacity-0 transition-opacity duration-150 group-hover/nav-item:opacity-100">
					{formatMoney(overdueAmount)}
				</span>
			</span>
		)
	}

	if (unpaid > 0) {
		return (
			<span className="grid text-xs font-medium tabular-nums leading-tight text-orange-600">
				<span className="col-start-1 row-start-1 transition-opacity duration-150 group-hover/nav-item:opacity-0">
					{t('settingsNav.invoicesUnpaidBadge', { count: unpaid })}
				</span>
				<span className="col-start-1 row-start-1 opacity-0 transition-opacity duration-150 group-hover/nav-item:opacity-100">
					{formatMoney(unpaidAmount)}
				</span>
			</span>
		)
	}

	return null
}
